import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Collapse,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import Grid2 from "@mui/material/Grid2";
import dayjs, { Dayjs } from "dayjs";
import { observer } from "mobx-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";

const DEFAULT_SYNC_LIMIT = 20;

function parseKnitSyncFiltersFromSearch(search: string) {
  const p = new URLSearchParams(search);
  const today = dayjs().startOf("day");
  const startParam = p.get("start_date");
  const endParam = p.get("end_date");
  const startDate =
    startParam && dayjs(startParam).isValid()
      ? dayjs(startParam).startOf("day")
      : today;
  const endDate =
    endParam && dayjs(endParam).isValid()
      ? dayjs(endParam).startOf("day")
      : today;
  const searchText = (p.get("search_text") || p.get("search") || "").trim();
  const pageRaw = parseInt(p.get("page") || "1", 10);
  const limitRaw = parseInt(
    p.get("limit") || String(DEFAULT_SYNC_LIMIT),
    10,
  );
  const page = Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw : 1;
  const limit =
    Number.isFinite(limitRaw) && limitRaw >= 1 ? limitRaw : DEFAULT_SYNC_LIMIT;

  return { startDate, endDate, searchText, page, limit };
}

function urlSearchParamsEqual(a: string, b: string) {
  const pa = new URLSearchParams(a.startsWith("?") ? a.slice(1) : a);
  const pb = new URLSearchParams(b.startsWith("?") ? b.slice(1) : b);
  const keys = new Set(
    Array.from(pa.keys()).concat(Array.from(pb.keys())),
  );
  const keyList = Array.from(keys);
  for (let i = 0; i < keyList.length; i++) {
    const key = keyList[i];
    if (pa.get(key) !== pb.get(key)) return false;
  }
  return true;
}

const HrmsGetKnitSync: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { hrmsStore, userStore } = useStores();

  const canViewHrms = userStore.hasPermission([EmployeeRoles.VIEW_HRMS]);
  const [startDate, setStartDate] = useState<Dayjs | null>(() =>
    parseKnitSyncFiltersFromSearch(location.search).startDate,
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(() =>
    parseKnitSyncFiltersFromSearch(location.search).endDate,
  );
  const [searchTextInput, setSearchTextInput] = useState(
    () => parseKnitSyncFiltersFromSearch(location.search).searchText,
  );
  const [searchText, setSearchText] = useState(
    () => parseKnitSyncFiltersFromSearch(location.search).searchText,
  );
  const debouncedSearchInput = useDebounce(searchTextInput, 500);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const customerId = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return (queryParams.get("customerId") || queryParams.get("customer_id") || "").trim();
  }, [location.search]);

  useLayoutEffect(() => {
    if (!canViewHrms) return;
    const { page, limit } = parseKnitSyncFiltersFromSearch(location.search);
    hrmsStore.setSyncEventsLimit(limit);
    hrmsStore.setSyncEventsPage(page);
  }, [canViewHrms, hrmsStore, location.search]);

  useEffect(() => {
    if (!canViewHrms) return;
    const next = new URLSearchParams(location.search);
    if (startDate) {
      next.set("start_date", startDate.format("YYYY-MM-DD"));
    } else {
      next.delete("start_date");
    }
    if (endDate) {
      next.set("end_date", endDate.format("YYYY-MM-DD"));
    } else {
      next.delete("end_date");
    }
    if (searchText.trim()) {
      next.set("search_text", searchText.trim());
    } else {
      next.delete("search_text");
      next.delete("search");
    }
    next.set("page", String(hrmsStore.syncEventsPage));
    next.set("limit", String(hrmsStore.syncEventsLimit));
    const nextStr = next.toString();
    const currStr = location.search.replace(/^\?/, "");
    if (!urlSearchParamsEqual(nextStr, currStr)) {
      navigate({ search: nextStr ? `?${nextStr}` : "" }, { replace: true });
    }
  }, [
    canViewHrms,
    startDate,
    endDate,
    searchText,
    hrmsStore.syncEventsPage,
    hrmsStore.syncEventsLimit,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!canViewHrms) return;
    hrmsStore
      .getSyncEvents({
        ...(customerId ? { customerId } : {}),
        ...(searchText.trim() ? { search_text: searchText.trim() } : {}),
        ...(startDate ? { start_date: startDate.format("YYYY-MM-DD") } : {}),
        ...(endDate ? { end_date: endDate.format("YYYY-MM-DD") } : {}),
        page: hrmsStore.syncEventsPage,
        limit: hrmsStore.syncEventsLimit,
      })
      .catch(() => {});
  }, [
    canViewHrms,
    customerId,
    searchText,
    startDate,
    endDate,
    hrmsStore,
    hrmsStore.syncEventsPage,
    hrmsStore.syncEventsLimit,
  ]);

  const getCellValue = (value: unknown) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const handleSearch = () => {
    setSearchText(searchTextInput.trim());
    hrmsStore.setSyncEventsPage(1);
  };

  const handleClearFilters = () => {
    const today = dayjs().startOf("day");
    setSearchTextInput("");
    setSearchText("");
    setStartDate(today);
    setEndDate(today);
    hrmsStore.setSyncEventsLimit(DEFAULT_SYNC_LIMIT);
    hrmsStore.setSyncEventsPage(1);
  };

  useEffect(() => {
    const newSearch = debouncedSearchInput.trim();
    if (newSearch === searchText) return;
    setSearchText(newSearch);
    hrmsStore.setSyncEventsPage(1);
  }, [debouncedSearchInput, hrmsStore, searchText]);

  if (!canViewHrms) {
    return <PermissionBlockLabel title="GetKnit Sync" />;
  }

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Box sx={{ p: 3 }}>
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            boxShadow:
              "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <CustomTypography variant="heading2" fontWeight={700}>
              HRMS GetKnit Sync
            </CustomTypography>
          </Box>
          <Paper
            variant="outlined"
            sx={{
              mb: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: isFiltersExpanded ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="body2" fontWeight={600}>
                Search & Filters
              </CustomTypography>
              <IconButton size="small">
                {isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                    flexWrap: "nowrap",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 320 }}>
                    <FormTextField
                      id="getknit-sync-search"
                      placeholder="Search by customer name or customer id"
                      type="search"
                      variant="outlined"
                      sx={{ mb: 0 }}
                      value={searchTextInput}
                      onChange={(_, value) => setSearchTextInput(value || "")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                    />
                  </Box>
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleSearch}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                </Box>
              </Box>
              <Box sx={{ p: 2, pt: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ width: 260 }}>
                  <DateRangePickerNew
                    onRangeSelect={(range) => {
                      setStartDate(range.startDate);
                      setEndDate(range.endDate);
                      hrmsStore.setSyncEventsPage(1);
                    }}
                    startDate={startDate}
                    endDate={endDate}
                    disableFutureDates={false}
                  />
                  </Box>
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear"
                    onClick={handleClearFilters}
                    customStyles={{ minWidth: 120 }}
                  />
                </Box>
              </Box>
            </Collapse>
          </Paper>

          {hrmsStore.syncEventsErrorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {hrmsStore.syncEventsErrorMessage}
            </Alert>
          )}

          {hrmsStore.isLoadingSyncEvents ? (
            <Box
              sx={{
                minHeight: "35vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              sx={{
                marginTop: "0.6rem",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <TableContainer sx={{ maxHeight: window.innerHeight - 350 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor:
                          theme.palette.customColors?.lightGreyColor ||
                          theme.palette.action.hover,
                        "& th": {
                          py: 1,
                          height: 48,
                        },
                      }}
                    >
                      <AppTableHeaderCell title="ID" sx={{ minWidth: 90 }} />
                      <AppTableHeaderCell title="Run ID" sx={{ minWidth: 160 }} />
                      <AppTableHeaderCell title="Job ID" sx={{ minWidth: 160 }} />
                      <AppTableHeaderCell
                        title="Integration ID"
                        sx={{ minWidth: 220 }}
                      />
                      <AppTableHeaderCell title="Customer ID" sx={{ minWidth: 130 }} />
                      <AppTableHeaderCell
                        title="Customer Name"
                        sx={{ minWidth: 220 }}
                      />
                      <AppTableHeaderCell title="Processed" sx={{ minWidth: 110 }} />
                      <AppTableHeaderCell title="Emitted" sx={{ minWidth: 110 }} />
                      <AppTableHeaderCell title="Complete" sx={{ minWidth: 110 }} />
                      <AppTableHeaderCell title="Failed" sx={{ minWidth: 110 }} />
                      <AppTableHeaderCell title="Created At" sx={{ minWidth: 180 }} />
                      <AppTableHeaderCell title="Last Updated" sx={{ minWidth: 180 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hrmsStore.syncEvents.map((row, index) => (
                      <TableRow
                        key={`${row.id}-${index}`}
                        hover
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: theme.palette.action.hover,
                          },
                          "& td": {
                            fontSize: "12px",
                            py: 1,
                            height: 48,
                            lineHeight: 1.2,
                          },
                        }}
                      >
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.id)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.sync_run_id)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.sync_job_id)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.x_knit_integration_id)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.customer_id)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.customer_name)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.processed)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.emitted)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.complete)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.failed)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.created_at)}
                        </AppTableRowCell>
                        <AppTableRowCell sx={{ textAlign: "center" }}>
                          {getCellValue(row.updated_at)}
                        </AppTableRowCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {hrmsStore.syncEvents.length === 0 &&
                !hrmsStore.isLoadingSyncEvents && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      p: 4,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      backgroundColor:
                        theme.palette.customColors?.grayBackGroundColor ||
                        theme.palette.action.hover,
                    }}
                  >
                    <CustomTypography
                      variant="heading6"
                      fontWeight={600}
                      color={theme.palette.customColors?.greyTextColor}
                    >
                      No sync events found.
                    </CustomTypography>
                  </Box>
                )}

              <AppTablePagenation
                  fontSize="12px"
                  totalCount={hrmsStore.syncEventsTotalCount}
                  limit={hrmsStore.syncEventsLimit}
                  pageNo={hrmsStore.syncEventsPage}
                  onPageChange={(_, newPage) =>
                    hrmsStore.setSyncEventsPage(newPage)
                  }
                  handleRowsChange={(newLimit) =>
                    hrmsStore.setSyncEventsLimit(newLimit)
                  }
                />
            </Paper>
          )}
        </Paper>
      </Box>
    </Grid2>
  );
};

export default observer(HrmsGetKnitSync);
